/*
 * Copyright (c) Funtip GbR 2017.
 * Simon Oldeboershuis, Perry Prust
 */

import { push } from '@lagunovsky/redux-react-router';
import * as R from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GroupInternalHighscoreLink } from '../../links/GroupInternalHighscoreLink';
import { UserGroupAdminLink } from '../../links/UserGroupAdminLink';
import { withBorder } from '../../reactutils/withBorder';
import { withNavLocation } from '../../reactutils/withNavLocation';
import { PagingComponent } from '../../reactutils/withPagination';
import { SearchComponent } from '../../reactutils/withSearch';
import { FuntipTitle } from '../../reactutils/withTitle';
import { TournamentRoundAndTournamentSelectionParams } from '../../reactutils/withTournamentAndTournamentRoundSelection';
import { GroupHighscore } from '../../services/betting';
import { SubMenuOverallHihgscores } from '../BettingApp/SubMenuOverallHighscores';
import { LoadingIndicatorContent } from '../common/LoadingIndicatorSpinner';
import { usePathSegment } from '../SysAdminApp/CacheStatisticsComponent';
import { TournamentSelectionComponent } from '../TournamentSelectionComponent';
import { tournamentSelectionChangeTournamentRound } from '../TournamentSelectionComponent/actions';
import { TournamentRoundSelectionComponent2 } from '../TournamentSelectionComponent/TournamentRoundSelectionComponent';
import {
    groupHighscoresLoadWithoutProps,
    groupHighscoresSetTrId,
    pageDownGroupHighscores,
    pageFirstGroupHighscores,
    pageLastGroupHighscores,
    pageUpGroupHighscores,
    searchGroupHighscores,
} from './actions';
import {
    userGroupHighscoresCountSelector,
    userGroupHighscoresCurrentPosSelector,
    userGroupHighscoresEntitiesSelector,
    userGroupHighscoresIsLoadingSelector,
    userGroupHighscoresPageSizeSelector,
    userGroupHighscoresSearchStringSelector,
    userGroupHighscoresTrIdSelector,
} from './selectors';

const UserGroupHighscoresComponent: React.FC<{}> = () => {
    const highscores = useSelector(userGroupHighscoresEntitiesSelector);
    const selectedTournamentRoundId = useSelector(userGroupHighscoresTrIdSelector);
    const isLoading = useSelector(userGroupHighscoresIsLoadingSelector);

    const [trId] = usePathSegment(3);

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (trId) {
            dispatch(groupHighscoresSetTrId(trId));
            dispatch(tournamentSelectionChangeTournamentRound({ type: 'highscores', trId: trId }));
            dispatch(searchGroupHighscores(''));
        }
    }, [dispatch, trId]);

    return (
        <>
            <FuntipTitle
                title="Teamwertung"
                subTitle=""
                leftComponent={<TournamentSelectionComponent onTournamentChange={trParams.onTournamentIdChange} />}
                rightComponent={
                    <TournamentRoundSelectionComponent2
                        type="highscores"
                        trId={trId}
                        pathByTournamentRound={userGroupHighscorePath}
                    />
                }
            />
            <SubMenuOverallHihgscores activeSubMenu="group-highscore" />
            <div className="d-flex align-items-center justify-content-between flex-wrap">
                <SearchComponent
                    searchResultCountSelector={(state: any) => userGroupHighscoresCountSelector(state)}
                    searchStringSelector={(state: any) => userGroupHighscoresSearchStringSelector(state)}
                    onSearch={(s: string) => searchGroupHighscores(s)}
                    placeholder="Team Suche"
                />
                <PagingComponent
                    onForward={() => pageDownGroupHighscores()}
                    onFastForward={() => pageLastGroupHighscores()}
                    onBackward={() => pageUpGroupHighscores()}
                    onFastBackward={() => pageFirstGroupHighscores()}
                    searchResultCount={userGroupHighscoresCountSelector}
                    searchResultCurrentPos={userGroupHighscoresCurrentPosSelector}
                    searchResultPageSize={userGroupHighscoresPageSizeSelector}
                />
            </div>
            <table className="mt-1 table ">
                <thead>
                    <tr>
                        <th className="border-top-0 d-none d-sm-table-cell pe-1">Platz</th>
                        <th className="border-top-0 d-table-cell d-sm-none pe-1">Pl</th>
                        <th className="border-top-0 ps-0">Gruppe</th>
                        <th className="border-top-0 d-none d-sm-table-cell">Punkte</th>
                        <th className="border-top-0 d-table-cell d-sm-none">Pkt</th>
                        <th className="border-top-0 ">Tipps</th>
                        <th className="border-top-0 pe-0">&empty;</th>
                    </tr>
                </thead>
                <tbody>
                    {highscores.map((hs: GroupHighscore) => (
                        <tr key={hs.id}>
                            <td className="pe-1">{hs.rank}</td>
                            <td >
                                <LoadingIndicatorContent isLoading={isLoading}>
                                    <UserGroupAdminLink ugId={hs.userGroupId} maxWidth={100}>
                                        {hs.userGroupName}
                                    </UserGroupAdminLink>
                                </LoadingIndicatorContent>
                            </td>
                            <td>
                                <LoadingIndicatorContent isLoading={isLoading}>
                                    <GroupInternalHighscoreLink
                                        trId={selectedTournamentRoundId || ''}
                                        ugId={hs.userGroupId}
                                    >
                                        {hs.points}
                                    </GroupInternalHighscoreLink>
                                </LoadingIndicatorContent>
                            </td>
                            <td className="">{hs.bets}</td>
                            <td className="pe-0">{Number(hs.averagePoints).toFixed(2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};

export const userGroupListPath = () => '/b/user-group-list';
export const userGroupHighscorePath = (trId: string) => `/b/group-highscores/${trId}`;

const trParams: TournamentRoundAndTournamentSelectionParams = {
    type: 'highscores',
    pathByTournamentRound: () => userGroupHighscorePath,
    onTournamentRoundIdChange: (id: string) => push(userGroupHighscorePath(id)),
    onTournamentIdChange: (tId: string) => groupHighscoresLoadWithoutProps(),
};

export const UserGroupHighscoreScreen = R.compose(
    withBorder(),
    withNavLocation('overall-group-highscore')
)(UserGroupHighscoresComponent);
